import { useCallback, useId, useState } from "react";

import {
  Button,
  CheckSVG,
  CloseSVG,
  Icon,
  InfoModal,
  Input,
  LinkSVG,
  useModal,
} from "@newsela/angelou";
import { BubbleMenu } from "@tiptap/react";
import PropTypes from "prop-types";

import { buttonsStyle } from "../Buttons.style";
import {
  bubbleMenuButtonStyle,
  bubbleMenuStyle,
  inputStyle,
  linkButtonModalStyle,
  removeIconStyle,
  saveIconStyle,
} from "./LinkButton.style";

const propTypes = {
  editor: PropTypes.object,
};

const LinkButton = ({ editor }) => {
  const modalLabelId = useId();
  const [url, setUrl] = useState("");
  const [linkModalProps, setLinkModalActive, linkModalTriggerRef] =
    useModal(false);

  const isActive = editor.isActive("link");

  const handleCloseModal = () => {
    linkModalProps.closeModal();
    setUrl("");
  };

  const handleOpenModal = useCallback(() => {
    setUrl(editor.getAttributes("link").href || "");
    setLinkModalActive(true);
  }, [editor]);

  const handleShouldShowBubbleMenu = useCallback(
    ({ editor, from, to }) => {
      const isActiveLink = from === to && editor.isActive("link");

      // display the URL in the input if popup is open
      if (isActiveLink) {
        setUrl(editor.getAttributes("link").href || "");
      }

      return isActiveLink;
    },
    [editor],
  );

  const handleUrlChange = (e) => setUrl(e.target.value);

  const handleRemoveLink = useCallback(() => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setUrl("");
  }, [editor]);

  const handleSaveLink = useCallback(() => {
    if (url) {
      const rangeStart = editor.state.selection.$anchor.pos;
      const rangeEnd = editor.state.selection.$head.pos;

      // When text isn't highlighted, insert an anchor link.
      if (rangeStart === rangeEnd && !isActive) {
        editor
          .chain()
          .focus()
          .insertContent(url)
          .insertContent(" ")
          .setTextSelection({ from: rangeStart, to: rangeStart + url.length })
          .extendMarkRange("link")
          .setLink({ href: url, target: "_blank" })
          .run();
        return;
      }

      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url, target: "_blank" })
        .run();
    } else {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    }
  }, [editor, url]);

  return (
    <>
      <Button
        __cssFor={{ root: buttonsStyle(isActive) }}
        flavor={Button.flavors.incognito}
        onClick={handleOpenModal}
        ref={linkModalTriggerRef}
      >
        <Icon
          SvgComponent={LinkSVG}
          isDecorative={false}
          size={Icon.sizes.md}
          title="Link"
        />
      </Button>

      <BubbleMenu
        tippyOptions={{ duration: 150 }}
        editor={editor}
        shouldShow={handleShouldShowBubbleMenu}
      >
        <div css={bubbleMenuStyle}>
          <Input
            __cssFor={inputStyle}
            inputType={Input.inputTypes.url}
            label="Edit URL"
            onChange={handleUrlChange}
            value={url}
          />
          <Button
            __cssFor={bubbleMenuButtonStyle}
            flavor={Button.flavors.incognito}
            onClick={handleSaveLink}
          >
            <Icon
              __cssFor={saveIconStyle}
              SvgComponent={CheckSVG}
              isDecorative={false}
              size={Icon.sizes.md}
              title="Save link"
            />
          </Button>
          <Button
            __cssFor={bubbleMenuButtonStyle}
            flavor={Button.flavors.incognito}
            onClick={handleRemoveLink}
          >
            <Icon
              __cssFor={removeIconStyle}
              SvgComponent={CloseSVG}
              isDecorative={false}
              size={Icon.sizes.md}
              title="Remove link"
            />
          </Button>
        </div>
      </BubbleMenu>

      <InfoModal
        __cssFor={linkButtonModalStyle}
        footer={
          <>
            {Boolean(url) && (
              <Button
                flavor={Button.flavors.polarTertiary}
                onClick={() => {
                  handleRemoveLink();
                  handleCloseModal();
                }}
              >
                Remove
              </Button>
            )}
            <Button
              flavor={Button.flavors.primary}
              onClick={() => {
                handleSaveLink();
                handleCloseModal();
              }}
            >
              Save
            </Button>
          </>
        }
        labelId={modalLabelId}
        onBackgroundClick={handleCloseModal}
        onEsc={handleCloseModal}
        title="Link URL"
        {...linkModalProps}
      >
        <Input
          helperText="https://www.example.com"
          inputType={Input.inputTypes.url}
          label="URL"
          onChange={handleUrlChange}
          value={url}
        />
      </InfoModal>
    </>
  );
};

LinkButton.propTypes = propTypes;

export { LinkButton };
